/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-05
 */

// Add third-party dependencies.
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Stack,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';

// Add local dependencies.
import Header from '../../layout/Header';
import { YouTubeVideoButton } from '../../common';
import {
  useGetThemesQuery,
  useCreateThemeMutation,
  useUpdateThemeMutation,
} from '../../../api/studioThemeApi';
import { StudioTheme as StudioThemeType } from '../../../types/studioTheme';
import { useStudioThemeAccess } from '../../../hooks/useStudioThemeAccess';
import { useSessionAuth } from '../../../hooks/useSessionAuth';
import ColorPicker from '../../theme/ColorPicker';
import ThemePreview from '../../theme/ThemePreview';
import DynamicThemeProvider from '../../theme/DynamicThemeProvider';
import {
  DEFAULT_THEME_TOKENS,
  type ThemeTokens,
  type BaseThemeColors,
} from '../../../types/theme';

/**
 * Props for the StudioTheme component.
 */
interface StudioThemeProps {
  /**
   * Additional CSS classes.
   */
  className?: string;
}

/**
 * Studio Theme management page component.
 * Provides a comprehensive interface for customizing studio branding and theming.
 * Features side-by-side layout with controls on the left and live preview on the right.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The studio theme page component
 *
 * @example
 * return (
 *   <StudioTheme />
 * );
 */
const StudioTheme: React.FC<StudioThemeProps> = ({ className }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { studioId } = useParams<{ studioId: string }>();

  // Get authentication state
  const { isAuthenticated, isLoading: authLoading } = useSessionAuth();

  // Get studio theme access information
  const {
    eligibleStudios,
    hasThemeAccess,
    hasSingleStudio,
    hasMultipleStudios,
    isLoading: themeAccessLoading,
  } = useStudioThemeAccess();

  // Component state
  const [selectedStudioId, setSelectedStudioId] = useState<string | null>(null);
  const [currentTheme, setCurrentTheme] = useState<StudioThemeType | null>(
    null
  );

  // Theme editing state - Always initialize with valid default tokens
  const [themeTokens, setThemeTokens] =
    useState<ThemeTokens>(DEFAULT_THEME_TOKENS);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // RTK Query hooks
  const {
    data: themesData,
    isLoading: isLoadingThemes,
    error: themesError,
    refetch: refetchThemes,
  } = useGetThemesQuery(
    { studioId: selectedStudioId! },
    {
      skip: !selectedStudioId,
      refetchOnMountOrArgChange: true, // Always fetch fresh data on mount
    }
  );

  const [createTheme] = useCreateThemeMutation();
  const [updateTheme, { isLoading: isSaving }] = useUpdateThemeMutation();

  /**
   * Handle color changes and update theme tokens
   */
  const handleColorChange = React.useCallback(
    (colorKey: keyof BaseThemeColors, newColor: string) => {
      // Use current themeTokens or fall back to defaults if undefined
      const baseTokens = themeTokens || DEFAULT_THEME_TOKENS;

      const updatedColors = {
        ...baseTokens.colors,
        [colorKey]: newColor,
      };

      const updatedTokens: ThemeTokens = {
        ...baseTokens,
        colors: updatedColors,
      };

      setThemeTokens(updatedTokens);
      setHasUnsavedChanges(true);
    },
    [themeTokens]
  );

  /**
   * Handle text color changes (nested property)
   */
  const handleTextColorChange = React.useCallback(
    (newColor: string) => {
      const baseTokens = themeTokens || DEFAULT_THEME_TOKENS;

      const updatedTokens: ThemeTokens = {
        ...baseTokens,
        colors: {
          ...baseTokens.colors,
          text: {
            ...baseTokens.colors.text,
            primary: newColor,
          },
        },
      };

      setThemeTokens(updatedTokens);
      setHasUnsavedChanges(true);
    },
    [themeTokens]
  );

  /**
   * Save current theme changes
   */
  const saveTheme = React.useCallback(async () => {
    if (!currentTheme || !selectedStudioId) return;

    try {
      const result = await updateTheme({
        studioId: selectedStudioId,
        themeId: currentTheme.id,
        data: {
          tokens: themeTokens,
        },
      }).unwrap();

      // Update local state with saved theme
      setCurrentTheme(result);
      setThemeTokens(result.tokens); // Update form to show saved values
      setHasUnsavedChanges(false);

      // Force refetch to ensure we have latest data from server
      await refetchThemes();
    } catch (err) {
      console.error('Failed to save theme:', err);
    }
  }, [currentTheme, selectedStudioId, themeTokens, updateTheme, refetchThemes]);

  /**
   * Create default theme if none exists
   */
  const createDefaultTheme = React.useCallback(
    async (targetStudioId: string) => {
      try {
        const result = await createTheme({
          studioId: targetStudioId,
          data: {
            name: 'Default Theme',
            tokens: DEFAULT_THEME_TOKENS,
          },
        }).unwrap();

        setCurrentTheme(result);
        setThemeTokens(result.tokens);
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('Failed to create default theme:', err);
        // Fallback to default tokens
        setThemeTokens(DEFAULT_THEME_TOKENS);
        setHasUnsavedChanges(false);
      }
    },
    [createTheme]
  );

  /**
   * Reset theme to saved state
   */
  const resetTheme = React.useCallback(() => {
    if (currentTheme) {
      setThemeTokens(currentTheme.tokens);
      setHasUnsavedChanges(false);
    }
  }, [currentTheme]);

  // Handle studio selection and access control
  useEffect(() => {
    // Wait for authentication and theme access data to load
    if (authLoading || themeAccessLoading) {
      return;
    }

    // If user is not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // If user has no theme access, redirect to studios page
    if (!hasThemeAccess) {
      navigate('/studios');
      return;
    }

    // Handle studio selection based on access
    if (studioId) {
      // URL has studioId parameter (legacy route) - use it if user has access
      const hasAccessToStudio = eligibleStudios.some(s => s.id === studioId);
      if (hasAccessToStudio) {
        setSelectedStudioId(studioId);
      } else {
        // User doesn't have access to this studio - redirect to studios
        navigate('/studios');
      }
    } else if (hasSingleStudio) {
      // User has exactly one eligible studio - auto-select it
      const [studio] = eligibleStudios;
      setSelectedStudioId(studio.id);
    }
  }, [
    studioId,
    navigate,
    authLoading,
    isAuthenticated,
    themeAccessLoading,
    hasThemeAccess,
    hasSingleStudio,
    hasMultipleStudios,
    eligibleStudios,
  ]);

  // Handle theme data when it loads
  useEffect(() => {
    if (!themesData?.results) {
      return;
    }

    // Find current published theme or create a default draft
    const publishedTheme = themesData.results.find(
      (t: StudioThemeType) => t.status === 'published'
    );
    const draftTheme = themesData.results.find(
      (t: StudioThemeType) => t.status === 'draft'
    );

    let selectedTheme: StudioThemeType | null = null;
    if (publishedTheme) {
      selectedTheme = publishedTheme;
    } else if (draftTheme) {
      selectedTheme = draftTheme;
    } else if (themesData.results.length > 0) {
      [selectedTheme] = themesData.results;
    }

    if (selectedTheme) {
      setCurrentTheme(selectedTheme);

      // Always load theme tokens from database
      // This ensures saved changes are displayed when page loads
      setThemeTokens(selectedTheme.tokens);

      // Reset unsaved changes flag since we just loaded from database
      setHasUnsavedChanges(false);
    } else if (selectedStudioId) {
      // Create a default theme if none exists
      createDefaultTheme(selectedStudioId);
    }
  }, [themesData, selectedStudioId, createDefaultTheme]);

  // Show loading state
  if (isLoadingThemes && selectedStudioId) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="400px"
            gap={2}
          >
            <CircularProgress size={50} />
            <Typography variant="h6" color="text.secondary">
              Loading Studio Theme...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Show error state
  if (themesError) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {themesError && 'data' in themesError
              ? (themesError.data as any)?.detail || 'Failed to load themes'
              : 'Failed to load themes'}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <Container
        maxWidth="xl"
        className={className}
        sx={{
          py: 3,
          // Add bottom padding for mobile to account for potential bottom navigation
          pb: isMobile ? 10 : 3,
        }}
      >
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
            }}
          >
            Studio Theme Editor
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: 1.6,
              maxWidth: '600px',
            }}
          >
            Customize your studio&apos;s branding and colors to create a
            consistent look and feel across all your tools and embedded content.
          </Typography>
        </Box>

        {/* Tutorial Video Section */}
        <Box
          sx={{
            mb: 4,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Watch our step-by-step tutorial to learn how to customize your
            studio theme with colors and fonts.
          </Typography>
          <YouTubeVideoButton
            videoId="3DYvTkQC_ko"
            videoTitle="Studio Theme Tutorial"
            buttonText="Watch Tutorial"
            variant="outlined"
            size="small"
          />
        </Box>

        {/* Studio Selector Row with Action Buttons */}
        {(hasMultipleStudios || selectedStudioId) && (
          <Box
            sx={{
              mb: 4,
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
          >
            {/* Studio Selector - Only show for multiple studios */}
            {hasMultipleStudios && (
              <FormControl sx={{ flex: { xs: '1 1 100%', md: '0 0 400px' } }}>
                <InputLabel id="studio-selector-label">
                  Select Studio
                </InputLabel>
                <Select
                  labelId="studio-selector-label"
                  value={selectedStudioId || ''}
                  label="Select Studio"
                  onChange={(event: SelectChangeEvent<string>) => {
                    const selectedId = event.target.value;
                    setSelectedStudioId(selectedId);
                  }}
                  sx={{
                    backgroundColor: 'background.paper',
                  }}
                >
                  {eligibleStudios.map(studio => (
                    <MenuItem key={studio.id} value={studio.id}>
                      <Box>
                        <Typography variant="body1">{studio.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {studio.role === 'owner' ? 'Owner' : 'Manager'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Action Buttons Area */}
            {selectedStudioId && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                {/* Unsaved Changes Warning */}
                {hasUnsavedChanges && (
                  <Alert severity="warning" sx={{ py: 0.5, flex: '0 0 auto' }}>
                    You have unsaved changes
                  </Alert>
                )}

                {/* Save/Cancel Buttons */}
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={resetTheme}
                    disabled={!hasUnsavedChanges || isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={saveTheme}
                    disabled={!hasUnsavedChanges || isSaving || !currentTheme}
                    sx={{ minWidth: 100 }}
                  >
                    {isSaving ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* Info Alert for Multiple Studios */}
        {hasMultipleStudios && !selectedStudioId && (
          <Box sx={{ mb: 4 }}>
            <Alert severity="info" sx={{ maxWidth: 400 }}>
              Please select a studio to edit its theme.
            </Alert>
          </Box>
        )}

        {/* Content area - only show if studio is selected or auto-selected */}
        {selectedStudioId && (
          <>
            {/* Main Editor Content */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 3,
                minHeight: '600px',
              }}
            >
              {/* Controls Panel */}
              <Box
                sx={{
                  flex: { xs: '1', lg: '0 0 400px' },
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 3,
                  height: { lg: '80vh' },
                  overflow: 'auto',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Theme Controls
                </Typography>

                {/* Typography Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
                    Typography
                  </Typography>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="font-family-label">Font Family</InputLabel>
                    <Select
                      labelId="font-family-label"
                      value={
                        themeTokens?.typography?.fontFamily?.body ||
                        'Arial, Helvetica, sans-serif'
                      }
                      label="Font Family"
                      onChange={(event: SelectChangeEvent<string>) => {
                        const newFontFamily = event.target.value;

                        // Use current themeTokens or fall back to defaults if undefined
                        const baseTokens = themeTokens || DEFAULT_THEME_TOKENS;

                        const updatedTokens: ThemeTokens = {
                          ...baseTokens,
                          typography: {
                            ...baseTokens.typography,
                            fontFamily: {
                              ...baseTokens.typography.fontFamily,
                              body: newFontFamily,
                              heading: newFontFamily, // Use same for consistency
                            },
                          },
                        };
                        setThemeTokens(updatedTokens);
                        setHasUnsavedChanges(true);
                      }}
                      sx={{ backgroundColor: 'background.paper' }}
                    >
                      <MenuItem value="Arial, Helvetica, sans-serif">
                        Arial
                      </MenuItem>
                      <MenuItem value="Georgia, serif">Georgia</MenuItem>
                      <MenuItem value="'Times New Roman', serif">
                        Times New Roman
                      </MenuItem>
                      <MenuItem value="'Courier New', monospace">
                        Courier New
                      </MenuItem>
                      <MenuItem value="Verdana, sans-serif">Verdana</MenuItem>
                      <MenuItem value="'Trebuchet MS', sans-serif">
                        Trebuchet MS
                      </MenuItem>
                      <MenuItem value="Impact, sans-serif">Impact</MenuItem>
                      <MenuItem value="'Comic Sans MS', cursive">
                        Comic Sans MS
                      </MenuItem>
                      <MenuItem value="'Lucida Console', monospace">
                        Lucida Console
                      </MenuItem>
                      <MenuItem value="Tahoma, sans-serif">Tahoma</MenuItem>
                    </Select>
                  </FormControl>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    Choose a font family for your studio&apos;s text content
                  </Typography>
                </Box>

                {/* Background & Text Section - Moved to top */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
                    Background & Text
                  </Typography>

                  <ColorPicker
                    label="Background"
                    value={themeTokens?.colors?.background || '#ffffff'}
                    onChange={color => handleColorChange('background', color)}
                    contrastWith={
                      themeTokens?.colors?.text?.primary || '#000000'
                    }
                    helperText="Main page background color"
                  />

                  <ColorPicker
                    label="Surface"
                    value={themeTokens?.colors?.surface || '#f8f9fa'}
                    onChange={color => handleColorChange('surface', color)}
                    contrastWith={
                      themeTokens?.colors?.text?.primary || '#000000'
                    }
                    helperText="Card and component background color"
                  />

                  <ColorPicker
                    label="Text Color"
                    value={themeTokens?.colors?.text?.primary || '#000000'}
                    onChange={handleTextColorChange}
                    contrastWith={themeTokens?.colors?.background || '#ffffff'}
                    helperText="Primary text color for readability"
                    required
                  />
                </Box>

                {/* Primary Colors Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
                    Primary Colors
                  </Typography>

                  <ColorPicker
                    label="Primary"
                    value={themeTokens?.colors?.primary || '#1976d2'}
                    onChange={color => handleColorChange('primary', color)}
                    contrastWith={themeTokens?.colors?.background || '#ffffff'}
                    helperText="Main brand color for buttons and highlights"
                    required
                  />

                  <ColorPicker
                    label="Secondary"
                    value={themeTokens?.colors?.secondary || '#6c757d'}
                    onChange={color => handleColorChange('secondary', color)}
                    contrastWith={themeTokens?.colors?.background || '#ffffff'}
                    helperText="Accent color for secondary elements"
                  />

                  <ColorPicker
                    label="Neutral"
                    value={themeTokens?.colors?.neutral || '#e9ecef'}
                    onChange={color => handleColorChange('neutral', color)}
                    contrastWith={themeTokens?.colors?.background || '#ffffff'}
                    helperText="Neutral color for borders and dividers"
                  />
                </Box>

                {/* Status Colors Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
                    Status Colors
                  </Typography>

                  <ColorPicker
                    label="Success"
                    value={themeTokens?.colors?.success || '#28a745'}
                    onChange={color => handleColorChange('success', color)}
                    contrastWith={themeTokens?.colors?.background || '#ffffff'}
                    helperText="Color for success messages and confirmations"
                  />

                  <ColorPicker
                    label="Warning"
                    value={themeTokens?.colors?.warning || '#ffc107'}
                    onChange={color => handleColorChange('warning', color)}
                    contrastWith={themeTokens?.colors?.background || '#ffffff'}
                    helperText="Color for warnings and cautions"
                  />

                  <ColorPicker
                    label="Error"
                    value={themeTokens?.colors?.error || '#dc3545'}
                    onChange={color => handleColorChange('error', color)}
                    contrastWith={themeTokens?.colors?.background || '#ffffff'}
                    helperText="Color for errors and destructive actions"
                  />
                </Box>
              </Box>

              {/* Live Preview Panel */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 0, // Remove padding to let ThemePreview handle it
                  overflow: 'auto', // Allow scrolling for long preview content
                  maxHeight: { lg: '80vh' }, // Prevent too tall on large screens
                }}
              >
                {/* Debug: Let's simplify the condition since themeTokens is always initialized */}
                <DynamicThemeProvider
                  tokens={themeTokens}
                  applyCSSVars={false} // Don't apply globally, only to preview
                  resetCSS={false} // Don't reset CSS globally
                >
                  <ThemePreview title="Live Preview" compact={isMobile} />
                </DynamicThemeProvider>
              </Box>
            </Box>

            {/* Theme Status Information */}
            {currentTheme && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info">
                  Current theme: <strong>{currentTheme.name}</strong>
                  (Status: {currentTheme.status}, Version:{' '}
                  {currentTheme.version})
                  {hasUnsavedChanges && ' - Unsaved changes'}
                </Alert>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default StudioTheme;
